import React, { useContext, useEffect, useState } from "react";

import { useQuery } from "@apollo/client";
import { useSearchParams } from "react-router-dom";

import {
  Box,
  Caption,
  FilterBadge,
  Flex,
  OverlineMd,
  Pagination,
  Title,
} from "@olist/design-system";

import { ArticleItem } from "components/article-item/article-item";
import { Breadcrumb } from "components/breadcrumb";
import { ErrorHandling } from "components/errorHandling";
import { Loading } from "components/loading";
import { MixpanelContext } from "contexts";
import { productOptions } from "helpers/formatters";
import { InternalServerError } from "icons/error";
import { searchPages } from "services/gql/searchPages";

const LIMIT_ARTICLES_PER_PAGE = 8;

export function SearchArticles() {
  const { trackAgent } = useContext(MixpanelContext);
  const [currentPage, setCurrentPage] = useState(1);
  const [filter, setFilter] = useState("");
  const [searchParams] = useSearchParams();
  const query = searchParams.get("search");
  const product = searchParams.get("product");
  const { data, error, loading } = useQuery(searchPages, {
    variables: {
      query,
    },
  });
  const content = data?.pages?.search.results;
  document.title = "Busca - Central de ajuda";

  const handleFilterChange = (value: string) => {
    trackAgent.track(`Central de ajuda - Filtro de Listagem de Artigos`, {
      asset: "Central de ajuda",
      page: "search",
      action: "filter",
      filtro: value === "" ? "todos" : value,
      searchedText: query,
    });
    setFilter(value);
  };

  useEffect(() => {
    if (product) {
      setFilter(product);
    }
    trackAgent.track(`Central de ajuda - Listagem de Artigos`, {
      asset: "Central de ajuda",
      page: "search",
      action: "mount",
      filtro: product,
      searchedText: query,
    });
  }, [product, query]);

  if (loading) return <Loading />;
  if (error)
    return (
      <ErrorHandling
        image={<InternalServerError />}
        title="Algo inesperado aconteceu"
        subtitle="Ocorreu um erro inesperado ao buscar os artigos, mas fomos notificados!"
      />
    );

  const intialSlice =
    currentPage === 1 ? 0 : LIMIT_ARTICLES_PER_PAGE * (currentPage - 1);
  const lastSlice = currentPage * LIMIT_ARTICLES_PER_PAGE;
  const filterByProduct = ({ path }) => path.includes(filter || "");

  return (
    <>
      <Breadcrumb items={[{ text: "Resultados da busca" }]} />
      <Title type="default">
        Resultados da busca por:{" "}
        <Caption bold color="primary.base">
          {query}
        </Caption>
      </Title>
      <Box style={{ marginBottom: "12px" }}>
        <OverlineMd style={{ fontSize: 12 }}>
          {content.length} artigo{content.length > 1 ? "s" : ""} encontrado
          {content.length > 1 ? "s" : ""}
        </OverlineMd>
      </Box>
      <Flex gap="inline.small">
        <Caption bold>Filtrar resultados: </Caption>
        <FilterBadge
          id="todos"
          selected={filter === ""}
          onChange={() => handleFilterChange("")}
        >
          todos
        </FilterBadge>
        {productOptions.map(({ label, value }) => (
          <FilterBadge
            key={value}
            id={value}
            selected={value === filter}
            onChange={() => handleFilterChange(value)}
          >
            {label}
          </FilterBadge>
        ))}
      </Flex>
      {(content as Array<any>)
        .filter(filterByProduct)
        .slice(intialSlice, lastSlice)
        .map((article) => (
          <ArticleItem
            key={article.id}
            title={article.title}
            description={article.description || "Sem descrição"}
            path={`/kb/articles/${article.path}`}
          />
        ))}
      <Pagination
        currentPage={currentPage}
        onPageChange={(page) => setCurrentPage(page)}
        pageCount={Math.ceil(
          content.filter(filterByProduct).length / LIMIT_ARTICLES_PER_PAGE
        )}
      />
    </>
  );
}
